<script setup>
import {
  defineProps,
  defineEmits,
  computed,
  ref,
  getCurrentInstance,
  nextTick,
  defineExpose,
  onMounted,
} from 'vue';
import DisplayUserTooltip from '@/components/display-users/DisplayUserTooltip.vue';
import MsPopover from '@/components/popover/MsPopover.vue';
import { MsPopoverPosition } from '@/lib/vueFinalPopover/VueFinalPopover.vue';
import MsImage from '@/components/image/MsImage.vue';
import avatarApi from '@/apis/other/avatarAPI';

const props = defineProps({
  /**
   * Danh sách người dùng
   */
  userList: {
    type: Array,
    default: () => [],
  },
  showOnlyName: {
    type: Boolean,
    default: false,
  },
  /**
   * Độ rộng
   */
  width: {
    type: [Number, String],
    default: 0,
  },
  /**
   * Số item tối đa đc hiển thị khi multiple = true
   */
  maxItemDisplay: {
    type: Number,
    default: 5,
  },
  /**
   * Có hiện nút thêm người ko (chỉ dùng khi multiple = true)
   */
  hasAddBtn: {
    type: Boolean,
    default: false,
  },
  /**
   * Có hiện nút Xóa ko
   */
  hasRemoveBtn: {
    type: Boolean,
    default: false,
  },
  /**
   * Có đóng khi hover vào popover ko
   */
  isCloseTooltipWhenHoverPopover: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits([
  'onClickUserSingle',
  'onClickRemove',
  'onClickAddBtn',
]);

/**
 * Có active popover danh sách người dùng bị ẩn ko
 */
const isActivePopoverHiddenUsers = ref(false);

/**
 * Ref của btn thêm
 */
const addBtnRef = ref(null);

/**
 * Ref của người dùng đơn đc chọn
 */
const singleUserRef = ref(null);

/**
 * Ref của số người dùng bị ẩn
 */
const hiddenUsersRef = ref(null);

/**
 * Ref của popover danh sách người dùng bị ẩn
 */
const popoverHiddenUsersRef = ref(null);

const { proxy } = getCurrentInstance();

/**
 * Style của container
 * @author: BMThang(06/12/2022)
 */
const computedStyleContainer = computed(() => {
  return {
    width: props.width
      ? typeof props.width === 'string'
        ? props.width
        : `${props.width}px`
      : null,
  };
});

/**
 * Uid của component
 * @author: BMThang(06/12/2022)
 */
const computedUid = computed(() => {
  return proxy.$.uid;
});

/**
 * Class của các nút xóa
 * @author: BMThang(06/12/2022)
 */
const computedRemoveBtnClass = computed(() => {
  return `display-hidden-users-remove-btn${computedUid.value}`;
});

/**
 * Danh sách người dùng đc hiển thị
 * @author: BMThang(06/12/2022)
 */
const computedDisplayedUsers = computed(() => {
  if (props.userList.length > props.maxItemDisplay) {
    return props.userList.slice(0, props.maxItemDisplay - 1);
  }
  return props.userList.slice(0, props.maxItemDisplay);
});

/**
 * Danh sách người dùng bị ẩn
 * @author: BMThang(06/12/2022)
 */
const computedHiddenUsers = computed(() => {
  if (props.maxItemDisplay < 0) {
    return [];
  }
  if (props.userList.length > props.maxItemDisplay) {
    return props.userList.slice(props.maxItemDisplay - 1);
  }
  return props.userList.slice(props.maxItemDisplay);
});

/**
 * Số lượng người dùng bị ẩn
 * @author: BMThang(06/12/2022)
 */
const computedHiddenUsersCount = computed(() => {
  return `+${computedHiddenUsers.value.length}`;
});

/**
 * Có phải nhiều hay ko
 * @author: BMThang(09/02/2023)
 */
const multiple = computed(() => {
  return props.userList.length > 1 ? true : false;
})

/**
 * Lấy vị trí và đơn vị người dùng
 * @param {object} userInfo Thông tin người dùng
 * @returns vị trí và đơn vị của người dùng
 * @author: BMThang(06/12/2022)
 */
const getPositionAndOrg = (userInfo) => {
  let result = '';
  if (userInfo.JobTitleName) {
    result = userInfo.JobTitleName;
  }
  if (userInfo.OrganizationUnitName && userInfo.OrganizationUnitID) {
    if (result) {
      result = `${result} - ${userInfo.OrganizationUnitName}`;
    } else {
      result = userInfo.OrganizationUnitName;
    }
  }
  if (
    userInfo.OrganizationName &&
    userInfo.OrganizationID &&
    userInfo.OrganizationUnitID !== userInfo.OrganizationID
  ) {
    if (result) {
      result = `${result} - ${userInfo.OrganizationName}`;
    } else {
      result = userInfo.OrganizationName;
    }
  }

  return result;
};
onMounted(()=>{
})
/**
 * Lấy đơn vị người dùng
 * @param {object} userInfo Thông tin người dùng
 * @returns Đơn vị của người dùng
 * @author: BMThang(06/12/2022)
 */
const getOrg = (userInfo) => {
  let result = '';
  if (userInfo.OrganizationUnitName && userInfo.OrganizationUnitID) {
    if (result) {
      result = `${result} - ${userInfo.OrganizationUnitName}`;
    } else {
      result = userInfo.OrganizationUnitName;
    }
  }
  if (
    userInfo.OrganizationName &&
    userInfo.OrganizationID &&
    userInfo.OrganizationUnitID !== userInfo.OrganizationID
  ) {
    if (result) {
      result = `${result} - ${userInfo.OrganizationName}`;
    } else {
      result = userInfo.OrganizationName;
    }
  }

  return result;
};

/**
 * Lấy avatar người dùng
 * @param {object} userInfo Thông tin người dùng
 * @param {number} size Kích thước ảnh
 * @returns avatar người dùng
 * @author: BMThang(06/12/2022)
 */
const getUserAvatar = (userInfo, size = 32) => {
  return avatarApi.getAvatarLink(userInfo.MISAID, size);
};

/**
 * Emit event onClickUserSingle khi nhấn vào người dùng đc chọn
 * @param {object} e event object
 * @author: BMThang(06/12/2022)
 */
const handleClickUserSingle = (e) => {
  // Nếu bấm vào nút xóa thì ko làm j
  if (e.target.classList.contains('display-users-remove')) {
    return;
  }
  emits('onClickUserSingle', props.userList[0]);
};

/**
 * Emit event onClickRemove khi nhấn vào nút xóa người dùng
 * @param {object} userInfo thông tin người dùng
 * @author: BMThang(06/12/2022)
 */
const handleClickRemoveUser = (userInfo) => {
  emits('onClickRemove', userInfo);
  nextTick(() => {
    // Nếu ko còn người dùng nào bị ẩm thì đóng popover rồi out
    if (computedHiddenUsers.value.length === 0) {
      closePopoverHiddenUsers();
      return;
    }
    // Nếu còn người dùng thì tính lại vị trí popover
    if (
      typeof popoverHiddenUsersRef.value?.calculatePopoverPosition ===
      'function'
    ) {
      popoverHiddenUsersRef.value.calculatePopoverPosition();
    }
  });
};

/**
 * Emit event onClickAddBtn khi bấm nút thêm người dùng
 * @author: BMThang(06/12/2022)
 */
const handleClickAddBtn = () => {
  emits('onClickAddBtn');
};

/**
 * Đóng popover
 * @author: BMThang(06/12/2022)
 */
const closePopoverHiddenUsers = () => {
  isActivePopoverHiddenUsers.value = false;
};

/**
 * Mở popover
 * @author: BMThang(06/12/2022)
 */
const openPopoverHiddenUsers = () => {
  isActivePopoverHiddenUsers.value = true;
};

/**
 * Đóng popover khi di chuột ra khỏi content và popover
 * @param {object} e event object
 * @author: BMThang(06/12/2022)
 */
const handleMouseLeave = (e) => {
  console.log(popoverHiddenUsersRef.value)
  if (popoverHiddenUsersRef.value?.popoverRef?.contains(e.toElement)) {
    return;
  }

  closePopoverHiddenUsers();
};

defineExpose({
  addBtnRef,
  singleUserRef,
});
</script>

<template>
  <div
    v-if="userList.length > 0"
    class="display-users"
    :style="computedStyleContainer"
  >
    <!-- Hiển thị khi có 1 người -->
    <display-user-tooltip
      v-if="!multiple"
      :user-info="userList[0]"
      :is-close-when-hover-popover="isCloseTooltipWhenHoverPopover"
    >
      <div 
        ref="singleUserRef"
        :class="[
          'display-users-single',
          {'display-users-single-has-remove': hasRemoveBtn}
        ]"
        @click="handleClickUserSingle"
      >
        <!-- Avatar -->
        <div class="display-users-avatar">
          <div class="display-users-avatar-wrapper">
            <MsImage :src="getUserAvatar(userList[0])" :alt="userList[0]?.FullName" />
          </div>
        </div>

        <!-- Info -->
        <div class="display-users-info">
          <div class="display-users-name" :title="userList[0]?.FullName">
            {{ userList[0]?.FullName }}
          </div>
          <div v-if="!showOnlyName" class="display-users-position" :title="getPositionAndOrg(userList[0])">
            {{ getPositionAndOrg(userList[0]) }}
          </div>
        </div>

        <!-- Delete -->
        <div 
          v-show="hasRemoveBtn" 
          class="display-users-remove-single icon20 ic_delete-red" 
          :title="$t('i18nCommon.command.delete')" 
          @click="handleClickRemoveUser(userList[0])" 
        />
      </div>
    </display-user-tooltip>

    <!-- Hiển thị khi có nhiều người -->
    <div 
      v-else 
      class="display-users-multiple"
    >
      <!-- Danh sách người đc hiển thị -->
      <display-user-tooltip
        v-for="(user) in computedDisplayedUsers"
        :key="user.UserID"
        :user-info="user"
        :is-close-when-hover-popover="isCloseTooltipWhenHoverPopover"
      >
        <div class="display-users-avatar">
          <div class="display-users-avatar-wrapper">
            <MsImage :src="getUserAvatar(user)" :alt="user?.FullName" />
          </div>
          <div 
            v-if="hasRemoveBtn" 
            class="display-users-remove" 
            :title="$t('i18nCommon.command.delete')"
            @click="handleClickRemoveUser(user)" 
          />
        </div>
      </display-user-tooltip>

      <!-- Số người bị ẩn -->
      <div
        v-if="computedHiddenUsers.length"
        ref="hiddenUsersRef"
        class="display-users-multiple-hidden-number"
        @mouseenter="openPopoverHiddenUsers"
        @mouseleave="handleMouseLeave"
      >
        {{ computedHiddenUsersCount }}
      </div>

      <!-- Popover số người bị ẩn -->
      <ms-popover
        ref="popoverHiddenUsersRef"
        :target="hiddenUsersRef"
        :is-active="isActivePopoverHiddenUsers"
        :width="314"
        :max-height="386"
        :position="{
          y: MsPopoverPosition.Top,
        }"
        :custom-style="{
          padding: '8px 0',
        }"
        :except-classes="[computedRemoveBtnClass]"
        @onMouseLeave="handleMouseLeave"
      >
        <div class="display-hidden-users-list">
          <div 
            v-for="(user) in computedHiddenUsers"
            :key="user.UserID"
            class="display-hidden-users"
          >
            <!-- Avatar -->
            <div class="display-hidden-users-avatar">
              <div class="display-hidden-users-avatar-wrapper">
                <MsImage :src="getUserAvatar(user, 40)" :alt="user.FullName" />
              </div>
            </div>

            <!-- Info -->
            <div 
              :class="[
                'display-hidden-users-info',
                {'display-hidden-users-info-has-remove-btn': hasRemoveBtn}
              ]"
            >
              <div class="display-hidden-users-name" :title="user.FullName">
                {{ user.FullName }}
              </div>
              <div v-if="user.JobTitleName" class="display-hidden-users-position" :title="user.JobTitleName">
                {{ user.JobTitleName }}
              </div>
              <div class="display-hidden-users-org" :title="getOrg(user)">
                {{ getOrg(user) }}
              </div>
            </div>

            <!-- Remove btn -->
            <div 
              v-if="hasRemoveBtn" 
              :title="$t('i18nCommon.command.delete')"
              :class="[
                'display-hidden-users-remove-btn',
                computedRemoveBtnClass,
              ]" 
              @click="handleClickRemoveUser(user)" 
            />
          </div>
        </div>
      </ms-popover>

      <!-- Nút thêm người -->
      <div
        v-if="hasAddBtn"
        ref="addBtnRef"
        class="display-users-multiple-add-btn"
        @click="handleClickAddBtn"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.display-users {
  // Avatar
  .display-users-avatar {
    width: 32px;
    height: 32px;
    margin-right: 8px;
    position: relative;
    flex-shrink: 0;
    .display-users-avatar-wrapper {
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 50%;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        border-radius: 50%;
        overflow: hidden;
      }
    }
    .display-users-remove {
      position: absolute;
      top: -4px;
      right: -4px;
      width: 16px;
      height: 16px;
      cursor: pointer;
      background-image: url($ms-image-ic_times-red);
      background-position: center;
      background-size: 8px 8px;
      background-color: var(--white);
      background-repeat: no-repeat;
      box-shadow: $box-box-shadow;
      border-radius: 50%;
      transform: scale(0);
      transition: transform 0.2s ease;
    }
    &:hover {
      .display-users-remove {
        transform: scale(1);
      }
    }
  }

  // Single
  .display-users-single {
    display: flex;
    align-items: center;
    width: 100%;
    height: 38px;
    .display-users-info {
      width: calc(100% - 40px);
      div {
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        &.display-users-name {
          font-size: $font-size-body;
          font-weight: $font-weight-medium;
          margin-bottom: 2px;
        }
        &.display-users-position {
          font-size: $font-size-caption;
          font-weight: $font-weight-regular;
          color: var(--black-10);
        }
      }
    }
    .ic_delete-red {
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.2s ease, visibility 0.2s ease;
    }
    &.display-users-single-has-remove {
      .display-users-info {
        width: calc(100% - 60px);
      }
    }
    &:hover {
      .ic_delete-red {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  // Multiple
  .display-users-multiple {
    display: flex;
    align-items: center;
    // Avatar
    .display-users-avatar {
      margin-right: 4px;
    }
    // Btn số
    .display-users-multiple-hidden-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: #f6f6f6;
      cursor: pointer;
      font-size: $font-size-body;
      font-weight: $font-weight-bold;
      color: var(--primary);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    // Btn thêm
    .display-users-multiple-add-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-image: url($ms-image-ic_plus-grey);
      background-size: 14px 14px;
      background-repeat: no-repeat;
      background-position: center;
      cursor: pointer;
      border: 1px dashed var(--grey-60);
      margin-left: 4px;
    }
  }
}

// Hidden users
.display-hidden-users-list {
  width: 100%;
  height: 100%;
  max-height: 314px;
  overflow: auto;
  padding: 0 8px;
}

.display-hidden-users {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 6px 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  // Avatar
  .display-hidden-users-avatar {
    width: 40px;
    height: 40px;
    margin-right: 8px;
    position: relative;
    .display-hidden-users-avatar-wrapper {
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 50%;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        border-radius: 50%;
        overflow: hidden;
      }
    }
  }
  // Info
  .display-hidden-users-info {
    width: calc(100% - 48px);
    div {
      width: 100%;
      &.display-hidden-users-name {
        font-size: $font-size-body;
        font-weight: $font-weight-medium;
        margin-bottom: 4px;
      }
      &.display-hidden-users-org,
      &.display-hidden-users-position {
        font-size: $font-size-caption;
        font-weight: $font-weight-regular;
        color: var(--black-10);
      }
      &.display-hidden-users-position {
        margin-bottom: 4px;
      }
      &.display-hidden-users-org {
        margin-bottom: 2px;
        line-height: 16px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;  
        overflow: hidden;
      }
    }
    &.display-hidden-users-info-has-remove-btn {
      width: calc(100% - 62px);
    }
  }
  // Remove btn
  .display-hidden-users-remove-btn {
    margin-top: 14px;
    width: 14px;
    height: 14px;
    background-image: url($ms-image-ic_delete-red);
    background-size: 14px 14px;
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;
  }
  &:hover {
    background-color: var(--hover-bg);
  }
}
</style>